/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { showErrorThunk, showSuccessThunk } from '@spike/notifications-action';
import { RootState } from '../../store';
import { fetchOrganizationsThunk } from '../../actions/organization/OrganizationActions';
import { saveLocationThunk } from '../../actions/location/LocationActions';
import { Location, SaveLocation } from '../../model/location';

export interface LocationAccountOption {
    label: string;
    value: string;
    parentOrganizationId?: string | null;
}

/**
 * Account options for the AddLocationModal. Fetches organizations if they are not loaded yet.
 */
export const useLocationAccountOptions = (): Array<LocationAccountOption> => {
    const dispatch = useDispatch();
    const { organizations } = useSelector((state: RootState) => state.organizations);

    useEffect(() => {
        if (organizations.length === 0) {
            dispatch(fetchOrganizationsThunk() as any);
        }
    }, [dispatch, organizations.length]);

    return useMemo(
        () =>
            organizations.map(account => ({
                label: account.name,
                value: account.id.toString(),
                parentOrganizationId: account.parentOrganizationId?.toString() ?? null
            })),
        [organizations]
    );
};

/**
 * Saves a location and shows the success/error notification. Resolves to the saved location, or null when it failed.
 */
export const useSaveLocation = (): ((locationData: SaveLocation) => Promise<Location | null>) => {
    const dispatch = useDispatch();

    return useCallback(
        async (locationData: SaveLocation) => {
            try {
                const result: Location | undefined = await dispatch(saveLocationThunk(locationData) as any);

                // Check if the thunk succeeded by verifying we got a location back
                if (result) {
                    const isEditing = locationData.id !== undefined;
                    dispatch(
                        showSuccessThunk(
                            isEditing ? 'Location updated successfully' : 'Location created successfully'
                        ) as any
                    );
                    return result;
                }
                return null;
            } catch (error) {
                // eslint-disable-next-line no-console
                console.error('Error saving location:', error);
                dispatch(showErrorThunk('Failed to save location. Please try again.') as any);
                return null;
            }
        },
        [dispatch]
    );
};
